import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, NgZone, PipeTransform } from '@angular/core';
import { ChargingStationFreeChargeRequest } from '@dto/ocpp/chargingStationFreeChargeRequest';
import { ChargingStationPublicChargeRequest } from '@dto/ocpp/chargingStationPublicChargeRequest';
import { ChargingStationPrivateChargeRequest } from '@dto/ocpp/chargingStationPrivateChargeRequest';
import { ChargingStationRemoteTransactionRequest } from '@dto/chargingStationRemoteTransactionRequest';
import { FreechargeStatus } from '@dto/ocpp/freechargeStatus';
import { PublicchargeStatus } from '@dto/ocpp/publicchargeStatus';
import { PrivatechargeStatus } from '@dto/ocpp/privatechargeStatus';
import { ChargingStation } from '@models/charging-station';
import { Observable } from 'rxjs';
import { EntityService } from './entity.service';
import { RemoteStartTransactionResponse } from '@dto/ocpp/remoteStartTransactionResponse';
import { RemoteStopTransactionResponse } from '@dto/ocpp/remoteStopTransactionResponse';
import { ChargingPower } from '../../models/chargingPower';
import { ConnectorSystems } from '../../models/connectorSystems';
import { Manufacturer } from '../../models/manufacturer';
import { ChargingStationStausForCustomerDto } from '../../dto/chargingStationStausForCustomerDto';
import { ChargingstationGroupDto } from '../../dto/chragingstationGroupDto';
import { ChargingstationGroup } from '../../dto/chargingstationGroup';
import { ChargingStationDto } from '@dto/chargingStationDto';
import { UserForRfidAuth } from '@dto/userForRfidAuth';
import { ChargingStationByGroupDto } from '@dto/chargingStationByGroupDto';
import { ScannerInput } from '@dto/ocpp/scannerInput';
import { ChargingStationTableEntry } from '@dto/chargingStationTableEntry';
import { ChargingStationStatusHistoryDto } from '@dto/chargingStationStatusHistoryDto';
import { GetConfigurationRequest } from '@dto/ocpp/getConfigurationRequest';
import { ChargingStationStatus } from '@models/chargingStationStatus';
import { PatchDlmMode } from '../../dto/httpPatch/patchDlmMode';
import { SecondMeterCurrentResponseDto } from '../../dto/SecondMeterCurrentResponseDto';
import { MeterValueResponseDto } from '../../dto/ocpp/MeterValueResponseDto';
import { ChargeModeStatus } from '@dto/ocpp/chargeModeStatus';
import { Customer } from '../../models/customer';
import { Model } from '../../models/model';
import { Profile } from '../../models/profile';
import { ChargingStationAuthorizationResponse } from '../../models/ChargingStationAuthorizationResponse';
import { ModelDto } from '../../dto/modelDto';
import { ConfigurationRequest } from '@dto/ocpp/configurationRequest';
import { PublicRemoteStartRequestDto } from '@dto/publicRemoteStartRequestDto';
import { UpdateDetalisFromOcppResponse } from '@dto/updateDetalisFromOcppResponse';
import { UpdateFirmwareRequest } from '../../dto/ocpp/UpdateFirmwareRequest';
import { UpdateStatusFirmwareRequest } from '@dto/ocpp/UpdateStatusFirmwareRequest';
import { TableFilter } from '@models/tableFilter';
import { Address } from '../../models/address';
import { SupportTicketRequest } from '@dto/supportTicketRequest';
import { ChargingStationTableDto } from '../../dto/chargingStationTableDto';
import { ChangeAvailabilityRequest } from '@dto/ocpp/ChangeAvailabilityRequest';
import { ChargingPointDto } from '@dto/chargingPointDto';
import { statusUpdateEvent } from '@dto/statusUpdateEvent';
import { SyncFreeChargeModeFromOcppResponse } from '@dto/syncFreeChargeModeFromOcppResponse';

@Injectable({
  providedIn: 'root'
})
export class ChargingStationsService extends EntityService {

  public entityRoute = "chargingstations";
  public statusUpdateEventEmitter = new EventEmitter<statusUpdateEvent>();
  private activeSubscriptions: Map<string, EventSource> = new Map();

  constructor(http: HttpClient, @Inject('BASE_URL') baseUrl: string, private ngZone: NgZone) {
    super(http, baseUrl);
  }

  getChargingStationsTableOfCustomer(customerId: number): Observable<ChargingStationTableEntry[]> {
    return this.http.get<ChargingStationTableEntry[]>(this.baseUrl + 'api/customers/' + customerId + '/chargingStationsTable');
  }

  getChargingStationsOfCustomer(customerId: number): Observable<ChargingStationDto[]> {
    return this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/customers/' + customerId + '/chargingStations');
  }

  getChargingStationsOfCurrentCustomer(): Observable<ChargingStationDto[]> {
    return this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/chargingStations');
  }

  checkChargingStationsOfCurrentCustomerHasBidi(): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/hasbidi');
  }

  checkChargingStationsOfCustomerHasBidi(customerId: number): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/hasbidi/' + customerId);
  }

  getAllChargingStations(): Observable<ChargingStationTableDto[]> {
    return this.http.get<ChargingStationTableDto[]>(this.baseUrl + 'api/chargingStations/all');
  }

  getOfflineChargeStations(): Observable<ChargingStationDto[]> {
    return this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/chargingStations/offline');
  }

  getCountofChargingstations(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/chargingStations/count')
  }

  getChargingStationsOfCurrentUser(): Observable<ChargingStationDto[]> {
    return this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/users/chargingStations');
  }

  getChargingStationsTableOfCurrentCustomer(): Observable<ChargingStationTableEntry[]> {
    return this.http.get<ChargingStationTableEntry[]>(this.baseUrl + 'api/chargingStations/table');
  }

  async getChargingStationsOfCustomerAsync(customerId: number): Promise<ChargingStationDto[]> {
    return await this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/customers/' + customerId + '/chargingStations').toPromise();
  }

  async getChargingStationsOfCurrentCustomerAsync(): Promise<ChargingStationDto[]> {
    return await this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/chargingStations').toPromise();
  }

  getAllowedUsersAsync(id): Observable<UserForRfidAuth[]> {
    return this.http.get<UserForRfidAuth[]>(this.baseUrl + 'api/chargingStations/' + id + '/allowedUsers');
  }

  getChargingPointsStatus(): Observable<ChargingStationStausForCustomerDto> {
    return this.http.get<ChargingStationStausForCustomerDto>(this.baseUrl + 'api/chargingStations/chargingStationsStatusForCurrentUser');
  }

  getChargingPointStatusOfCustomer(customerId): Observable<ChargingStationStausForCustomerDto> {
    return this.http.get<ChargingStationStausForCustomerDto>(this.baseUrl + 'api/chargingStations/chargingStationsStatusOfCustomer/' + customerId);
  }

  getChargingPointStatus(id, connectorId): Observable<ChargingStationStatus> {
    return this.http.get<ChargingStationStatus>(this.baseUrl + 'api/chargingStations/' + id + '/' + connectorId + '/status');
  }

  getChargingPointsOfChargingStation(id): Observable<ChargingPointDto[]> {
    return this.http.get<ChargingPointDto[]>(this.baseUrl + 'api/chargingStations/' + id + '/chargingPoints');
  }

  getChargingStationPublicDetails(chargingStationId: string): Observable<ChargingStationDto> {
    return this.http.get<ChargingStationDto>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/publicDetails');
  }

  getChargingStationPublicDetailsByEvseId(chargingStationId: string): Observable<ChargingStationDto> {
    return this.http.get<ChargingStationDto>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/publicDetailsByEvseId');
  }

  getChargingStationHasFreechargeTransactions(id): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/' + id + '/hasFreechargeTransactions');
  }

  getChargingStationsWithFreechargeTransactions(): Observable<ChargingStationDto[]> {
    return this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/chargingStations/withFreechargeTransactions');
  }

  getGroupsWithChargingStations(chargingStations: ChargingStationDto[]): Observable<ChargingstationGroupDto[]> {
    let params = new HttpParams()
      .set('chargingStationIdsString', chargingStations.map(x => x.id).join(','));

    return this.http.get<ChargingstationGroupDto[]>(this.baseUrl + 'api/ChargeStationGroup/withChargingStations', { params: params });
  }

  getGroupsWithChargingStation(chargingStationId: string): Observable<ChargingstationGroupDto[]> {
    return this.http.get<ChargingstationGroupDto[]>(this.baseUrl + 'api/ChargeStationGroup/withChargingStation/' + chargingStationId);
  }

  getChargingstationGroups(): Observable<ChargingstationGroupDto[]> {
    return this.http.get<ChargingstationGroupDto[]>(this.baseUrl + 'api/ChargeStationGroup');
  }

  getChargingstationGroup(groupId: number): Observable<ChargingstationGroupDto> {
    return this.http.get<ChargingstationGroupDto>(this.baseUrl + 'api/ChargeStationGroup/' + groupId);
  }

  getChargingstationGroupsOfCustomer(customerId): Observable<ChargingstationGroupDto[]> {
    return this.http.get<ChargingstationGroupDto[]>(this.baseUrl + 'api/customers/' + customerId + '/chargingStationGroups');
  }

  async getChargingstationGroupsOfCustomerAsync(customerId): Promise<ChargingstationGroupDto[]> {
    return await this.http.get<ChargingstationGroupDto[]>(this.baseUrl + 'api/customers/' + customerId + '/chargingStationGroups').toPromise();
  }

  async getChargingstationGroupsAsync(): Promise<ChargingstationGroupDto[]> {
    return await this.http.get<ChargingstationGroupDto[]>(this.baseUrl + 'api/ChargeStationGroup').toPromise();
  }

  getChargingstationsByGroup(): Observable<ChargingStationByGroupDto[]> {
    return this.http.get<ChargingStationByGroupDto[]>(this.baseUrl + 'api/chargingStations/byGroups');
  }

  getChargingstationsOfCustomerByGroup(customerId: number): Observable<ChargingStationByGroupDto[]> {
    return this.http.get<ChargingStationByGroupDto[]>(this.baseUrl + 'api/customers/' + customerId + '/chargingStationsByGroup');
  }

  getChargingStationsOfGroup(groupId: number): Observable<ChargingStationDto[]> {
    return this.http.get<ChargingStationDto[]>(this.baseUrl + 'api/ChargeStationGroup/' + groupId + '/chargingStations',);
  }

  posttriggerAutomaticDiagnosticUpload(id): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/chargingStations/' + id + '/diagnostics');
  }

  getCustomer(): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.baseUrl + 'api/Customers');
  }

  getModels(): Observable<ModelDto[]> {
    return this.http.get<ModelDto[]>(this.baseUrl + 'api/chargeStationDetails/models');
  }

  getCountOfModels(): Observable<number> {
    return this.http.get<number>(this.baseUrl + 'api/chargeStationDetails/models/count');
  }

  getModel(id): Observable<Model> {
    return this.http.get<Model>(this.baseUrl + 'api/chargeStationDetails/model/' + id);
  }

  getProfiles(): Observable<Profile[]> {
    return this.http.get<Profile[]>(this.baseUrl + 'api/chargeStationDetails/profiles');
  }

  getProfile(id): Observable<Profile> {
    return this.http.get<Profile>(this.baseUrl + 'api/chargeStationDetails/profile/' + id);
  }

  getAdress(id): Observable<Address> {
    return this.http.get<Address>(this.baseUrl + 'api/chargeStationDetails/address/' + id);
  }

  getManufacturer(): Observable<Manufacturer[]> {
    return this.http.get<Manufacturer[]>(this.baseUrl + 'api/chargeStationDetails/manufacturer');
  }

  getConnectorSystems(): Observable<ConnectorSystems[]> {
    return this.http.get<ConnectorSystems[]>(this.baseUrl + 'api/chargeStationDetails/connectorSystems');
  }

  getChargingPower(): Observable<ChargingPower[]> {
    return this.http.get<ChargingPower[]>(this.baseUrl + 'api/chargeStationDetails/chargingPower');
  }

  getFreeChargeStatus(id): Observable<FreechargeStatus> {
    return this.http.get<FreechargeStatus>(this.baseUrl + 'api/chargingStations/' + id + '/freecharge');
  }

  getPublicChargeStatus(id): Observable<PublicchargeStatus> {
    return this.http.get<PublicchargeStatus>(this.baseUrl + 'api/chargingStations/' + id + '/publiccharge');
  }

  getPrivateChargeStatus(id): Observable<PrivatechargeStatus> {
    return this.http.get<PrivatechargeStatus>(this.baseUrl + 'api/chargingStations/' + id + '/privatecharge');
  }

  getChargeMode(id): Observable<ChargeModeStatus> {
    return this.http.get<ChargeModeStatus>(this.baseUrl + 'api/chargingStations/' + id + '/chargemode');
  }

  getMeterValue(transactionId: number): Observable<MeterValueResponseDto> {
    return this.http.get<MeterValueResponseDto>(this.baseUrl + 'api/chargingStations/' + transactionId + '/meter');
  }

  getScannerInput(id: string): Observable<ScannerInput> {
    return this.http.get<ScannerInput>(this.baseUrl + 'api/chargingStations/' + id + '/scanner');
  }

  getConfiguration(id: string, getConfigurationRequest: GetConfigurationRequest): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'api/chargingStations/' + id + '/getConfiguration', getConfigurationRequest);
  }

  postConfiguration(id: string, configurationRequest: ConfigurationRequest): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'api/chargingStations/' + id + '/postConfiguration', configurationRequest);
  }

  getFirmwareVersion(id: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/chargingStations/' + id + '/firmware');
  }

  getSecondMeterCurrent(id: string, connectorId: number): Observable<SecondMeterCurrentResponseDto> {
    return this.http.get<SecondMeterCurrentResponseDto>(this.baseUrl + 'api/chargingStations/' + id + '/secondMeter/' + connectorId);
  }

  getCharingStationsHistoryTableOfCustomer(customerId: number, filter: TableFilter = {}): Observable<ChargingStationStatusHistoryDto[]> {
    return this.http.post<ChargingStationStatusHistoryDto[]>(this.baseUrl + 'api/customers/' + customerId + '/chargingStationsHistoryTable', filter);
  }

  getCustomerIdOfChargingStation(id: string): Observable<number> {
    return this.http.get<number>(this.baseUrl + 'api/chargingStations/' + id + '/customerId');
  }

  checkCanRemoteStart(id: string): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/' + id + '/can-remote-start');
  }

  UpdateDlmSettingsFromOcpp(id: string): Observable<boolean> {
    return this.http.put<boolean>(this.baseUrl + 'api/chargingStations/' + id + '/configuration/dlmSettings', null);
  }

  SetStatus(id: string, connectorId: number, statusId: number): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'api/chargingStations/' + id + '/' + connectorId + '/setStatus/' + statusId, null);
  }

  UpdateDetailsFromOcpp(id: string): Observable<UpdateDetalisFromOcppResponse> {
    return this.http.put<UpdateDetalisFromOcppResponse>(this.baseUrl + 'api/chargingStations/' + id + '/configuration/details', null);
  }

  syncFreeChargeModeFromOcpp(id: string): Observable<SyncFreeChargeModeFromOcppResponse> {
    return this.http.post<SyncFreeChargeModeFromOcppResponse>(this.baseUrl + 'api/chargingStations/' + id + '/sync-free-charge', null);
  }

  getChargingStationAuthorizationResponse(id: string): Observable<ChargingStationAuthorizationResponse> {
    return this.http.get<ChargingStationAuthorizationResponse>(this.baseUrl + 'api/chargingStations/' + id + '/ChargingStationAuthorizationResponse');
  }

  resetChargingstationPassword(id): Observable<boolean> {
    return this.http.patch<boolean>(this.baseUrl + 'api/chargingStations/' + id + '/resetpassword', null);
  }

  reserveChargingStation(id, connectorId): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/' + id + '/' + connectorId + '/reservation')
  }

  cancelReservationChargingStation(id, connectorId): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/' + id + '/' + connectorId + '/cancel-reservation')
  }

  resetChargingstation(id): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/reset/' + id);
  }

  checkOnlineStatus(id): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/chargingStations/check/'+ id);
  }

  getOfflineTimesSince24h(id): Observable<number> {
    return this.http.get<number>(this.baseUrl + 'api/chargingStations/' + id + '/offline-times');
  }

  createChargingstationForCustomer(customerId: number, chargingStation: ChargingStationDto): Observable<ChargingStationDto> {
    return this.http.post<ChargingStationDto>(this.baseUrl + 'api/customers/' + customerId + '/chargingStation', chargingStation);
  }

  configureOperatorPower(chargingStationId: string, power: number): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/configuration/chargingPower/' + power, null);
  }

  configureDlmMode(chargingStationId: string, patch: PatchDlmMode): Observable<ChargingStation> {
    return this.http.patch<any>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/dlm/', patch);

  }

  configureFreeCharge(chargingStationId: string, chargingStationFreeChargeRequest: ChargingStationFreeChargeRequest): Observable<FreechargeStatus> {
    return this.http.post<FreechargeStatus>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/configureFreeCharge', chargingStationFreeChargeRequest);
  }

  configurePublicCharge(chargingStationId: string, chargingStationPublicChargeRequest: ChargingStationPublicChargeRequest): Observable<PublicchargeStatus> {
    return this.http.post<PublicchargeStatus>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/configurePublicCharge', chargingStationPublicChargeRequest);
  }

  configurePrivateCharge(chargingStationId: string, chargingStationPrivateChargeRequest: ChargingStationPrivateChargeRequest): Observable<PrivatechargeStatus> {
    return this.http.post<PrivatechargeStatus>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/configurePrivateCharge', chargingStationPrivateChargeRequest);
  }

  remoteStartTransaction(chargingStationId: string, chargingPointConnectorId: number): Observable<RemoteStartTransactionResponse> {
    return this.http.post<RemoteStartTransactionResponse>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/' + chargingPointConnectorId + '/remoteStartTransaction', null);
  }

  remoteStartPublicTransaction(chargingStationId: string, paymentIntentId: string, request: PublicRemoteStartRequestDto): Observable<RemoteStartTransactionResponse> {
    return this.http.post<RemoteStartTransactionResponse>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/remoteStartPublicTransaction/' + paymentIntentId, request);
  }

  remoteStopTransaction(chargingStationId: string, chargingPointConnectorId: number): Observable<RemoteStopTransactionResponse> {
    return this.http.post<RemoteStopTransactionResponse>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/' + chargingPointConnectorId + '/remoteStopTransaction', null);
  }

  remoteStopTransactionOfTransactionId(chargingStationId: string, transactionId: number): Observable<RemoteStopTransactionResponse> {
    return this.http.post<RemoteStopTransactionResponse>(this.baseUrl + 'api/chargingStations/'+ chargingStationId +'/remoteStopTransaction/transaction/' + transactionId, null);
  }

  remoteStopPublicTransaction(chargingStationId: string, paymentIntentId: string, remoteStopRequest: ChargingStationRemoteTransactionRequest): Observable<RemoteStopTransactionResponse> {
    return this.http.post<RemoteStopTransactionResponse>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/remoteStopPublicTransaction/' + paymentIntentId, remoteStopRequest);
  }

  createChargingStationGroupForCurrentCustomer(chargingStationGroup: ChargingstationGroupDto): Observable<ChargingstationGroupDto> {
    return this.http.post<ChargingstationGroupDto>(this.baseUrl + 'api/ChargeStationGroup', chargingStationGroup);
  }

  createChargingStationGroupForCustomer(chargingStationGroup: ChargingstationGroupDto, customerId: number): Observable<ChargingstationGroupDto> {
    return this.http.post<ChargingstationGroupDto>(this.baseUrl + 'api/customers/' + customerId + '/chargingStationGroup', chargingStationGroup);
  }

  editChargingStationGroup(newChargingStationGroupDto: ChargingstationGroupDto, groupId: number): Observable<ChargingstationGroupDto> {
    return this.http.put<ChargingstationGroupDto>(this.baseUrl + 'api/ChargeStationGroup/' + groupId, newChargingStationGroupDto);
  }

  changeScannerMode(id: string, isActive: boolean): Observable<number> {
    let params = new HttpParams()
      .set('isActive', isActive.toString());

    return this.http.put<number>(this.baseUrl + 'api/ChargingStations/' + id + '/scanner', null, { params: params });
  }

  deleteChargingStationGroup(groupId: number): Observable<ChargingstationGroup> {
    return this.http.delete<ChargingstationGroup>(this.baseUrl + 'api/ChargeStationGroup/' + groupId);
  }

  updateChargingStationWhitelist(chargingStationId: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'api/ChargingStations/' + chargingStationId + '/updateChargingStationWhitelist', null);
  }

  updateChargingStationFirmware(updateRequest: UpdateFirmwareRequest, chargingStationId: string): Observable<UpdateFirmwareRequest> {
    return this.http.post<UpdateFirmwareRequest>(this.baseUrl + 'api/FirmwareStatusNotifications/' + chargingStationId + '/firmwareUpdate', updateRequest);
  }

  getChargingStationFirmwareUpdateStatus(chargingStationId: string): Observable<UpdateStatusFirmwareRequest> {
    return this.http.get<UpdateStatusFirmwareRequest>(this.baseUrl + 'api/FirmwareStatusNotifications/' + chargingStationId + '/getFirmwareUpdateStatus');
  }

  sendSupportTicket(supportTicket: SupportTicketRequest, chargingStationId: string): Observable<any> {
    return this.http.put<SupportTicketRequest>(this.baseUrl + 'api/ChargingStations/' + chargingStationId + '/sendTicket', supportTicket);
  }

  pushEVSEData(): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'api/statictriggers/pushEVSEData', null);
  }

  pushEVSEStatus(): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'api/statictriggers/pushEVSEStatus', null);
  }

  enableHubjectERoaming(id: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'api/ChargingStations/EnableHubjectERoaming/' + id, null);
  }

  disableHubjectERoaming(id: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'api/ChargingStations/DisableHubjectERoaming/' + id, null);
  }

  changeAvailability(id: string, request: ChangeAvailabilityRequest): Observable<boolean> {
    return this.http.put<any>(this.baseUrl + `api/ChargingStations/${id}/availability`, request);
  }

  search(searchTerm: string): Observable<ChargingStation[]> {
    return this.http.get<ChargingStation[]>(`${this.baseUrl}api/ChargingStations/search/${searchTerm}`);
  }

  updateChargingPointsStatusThroughSSE(event: statusUpdateEvent, chargingStation: ChargingStation | ChargingStationTableDto) {
    if (event.connectorId === 0) {
      chargingStation.statusId = event.status.id;
      chargingStation.status = event.status;
    } else {
      const chargingPoint = chargingStation.chargingPoints.find(cp => cp.connectorId === event.connectorId);
      if (chargingPoint) {
        chargingPoint.statusId = event.status.id;
        chargingPoint.status = event.status;
      }
    }
  }

  subscribeStatusUpdateToStations(stationIds: string[]): Observable<statusUpdateEvent> {
    const key = stationIds.sort().join(',');

    if (this.activeSubscriptions.has(key)) {
      return new Observable<statusUpdateEvent>((observer) => {
        observer.error(new Error('Subscription already exists'));
      });
    }

    return new Observable<statusUpdateEvent>((observer) => {
      this.http.post<any>(`${this.baseUrl}api/sse/status-update-event`, stationIds).subscribe(clientId => {
        const eventSource = new EventSource(`${this.baseUrl}api/sse/status-update-event/${clientId.result}`);
        this.activeSubscriptions.set(key, eventSource);

        eventSource.onmessage = (event) => {
          this.ngZone.run(() => {
            const data: statusUpdateEvent = JSON.parse(event.data);
            observer.next(data);
            this.statusUpdateEventEmitter.emit(data);
          });
        };

        eventSource.onerror = (error) => {
          this.ngZone.run(() => {
            observer.error(error);
            eventSource.close();
            this.activeSubscriptions.delete(key);
          });
        };

        return () => {
          eventSource.close();
          this.activeSubscriptions.delete(key);
        };
      }, error => {
        this.ngZone.run(() => {
          observer.error(error);
        });
      });
    });
  }

  unsubscribeStatusUpdateToStations(stationIds: string[]): void {
    const key = stationIds.sort().join(',');

    if (this.activeSubscriptions.has(key)) {
      const eventSource = this.activeSubscriptions.get(key);
      if (eventSource) {
        eventSource.close();
      }
      this.activeSubscriptions.delete(key);
    }
  }

  static mapStatusTooltip(statusId: number) : string {
    switch (statusId) {
      case 2:
        return "Diese Ladestation ist bereit für einen Ladevorgang."
      case 3:
        return "An dieser Ladestation lädt gerade ein Fahrzeug. Überprüfen Sie den Ladezustand über das Display des Fahrzeugs oder die Fahrzeug-App."
      case 6:
        return "Der Ladevorgang ist eingeleitet oder der Stecker gesteckt, aber der Ladevorgang konnte noch nicht gestartet werden.\n Überprüfen Sie den Sitz des Steckers oder führen Sie eine erneute Authentifizierung durch."
      case 8:
        return "Der Ladevorgang wurde vorübergehend gestoppt.\n Das Fahrzeug reagiert nicht auf die Ladestation.\n In den meisten Fällen ist das Fahrzeug vollständig geladen.\n"
      case 9:
        return "Der Ladevorgang wurde vorübergehend pausiert.\n Entweder ist das Fahrzeug vollgeladen oder das dynamische Lastmanagement (DLM) verteilt den Strom prioritär an andere Ladestationen.\n In seltenen Fällen kann auch eine vorübergehende Stromknappheit vorliegen."
      case 4:
        return "Ein Fehler ist aufgetreten. Bitte benachrichtigen Sie den Service im Bereich \"Support\" oder unter support@cubos.com und wählen Sie eine andere Ladestation."
      case 10:
        return "Die Ladestation ist momentan nicht verfügbar.\n Bitte versuchen Sie es später erneut oder wählen Sie eine andere Station."
      case 11:
        return "Zu dieser Ladestation besteht keine aktive Internetverbindung \n oder der Versorgungsstrom für die Ladestation ist ausgeschaltet."
      case 5:
        return "Der Ladevorgang ist beendet. Sie können Ihr Fahrzeug abkoppeln."
      case 7:
        return "Diese Ladestation ist derzeit für einen bestimmten Benutzer oder Fahrzeug reserviert. Bitte wählen Sie eine andere Station."
      case 30:
        return "Diese Ladestation ist derzeit gesperrt."
      case 31:
        return "Diese Ladestation ist nicht gesperrt."
  }
}

  static matches(chargingStation: ChargingStationDto, term: string, pipe?: PipeTransform) {
    return chargingStation.id?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.name?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.manufacturer?.name?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.model?.name.toLowerCase().includes(term.toLowerCase())
      || chargingStation.chargingPowerName?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.serialNumber?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.chargePointSerialNumber?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.location?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.localIP?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.chargingPoints.some(cp => cp.publicKey?.toLowerCase().includes(term.toLowerCase()));
  }

  static search(chargingStation: ChargingStationTableDto, term: string, pipe?: PipeTransform) {
    return chargingStation.id?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.name?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.manufacturer?.name?.toLowerCase().includes(term.toLowerCase())
      || chargingStation.model?.name.toLowerCase().includes(term.toLowerCase())
      || chargingStation.chargingPoints.some(cp => cp.status.name.toLowerCase().includes(term.toLowerCase()))
  }
}
